import { motion } from "framer-motion";
import heroVideo from "../assests/Anno.mp4"; // ✅ Ensure correct video path

export default function AnnotationLabeling() {
  return (
    <div className="bg-gray-50 text-gray-900">
      {/* Hero Section with Video Background */}
      <section className="relative w-full h-[70vh] text-white flex items-center justify-center text-center">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={heroVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="absolute inset-0 bg-black bg-opacity-60"></div>
        <div className="relative z-10 px-6">
          <motion.h1
            initial={{ opacity: 0, y: -40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-bold mb-4 tracking-tight font-orbitron"
          >
            Annotation & Labeling for Smarter AI
          </motion.h1>
          <p className="text-lg md:text-xl max-w-2xl mx-auto leading-relaxed font-raleway">
            We provide high-quality image, video, audio, and text annotation
            services that fuel the world’s most advanced AI and machine learning
            models.
          </p>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-6 text-center max-w-4xl mx-auto">
        <h2 className="text-4xl font-semibold mb-6 tracking-tight font-orbitron">
          Why Annotation & Labeling Matters
        </h2>
        <p className="text-lg text-gray-700 leading-relaxed font-raleway">
          Raw data is just noise without context. Annotation and labeling add
          the structure AI needs to learn, adapt, and make accurate decisions.
          Whether it’s detecting objects in images, transcribing conversations,
          or understanding human intent in text, annotation is the backbone of
          every successful AI system.
        </p>
        <ul className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 text-left text-base text-gray-700 leading-relaxed font-raleway">
          <li>✔ Accurate training datasets for reliable AI models</li>
          <li>✔ Domain-specific expertise for healthcare, automotive & retail</li>
          <li>✔ Scalable solutions for projects of any size</li>
          <li>✔ Human-in-the-loop for maximum precision</li>
        </ul>
      </section>

      {/* Services Section */}
      <section className="py-20 px-6 bg-gray-100">
        <h2 className="text-4xl font-semibold text-center mb-12 tracking-tight font-orbitron">
          Our Annotation Services
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 max-w-6xl mx-auto font-raleway">
          {[
            {
              title: "🖼 Image Annotation",
              desc: "Bounding boxes, polygons, semantic segmentation, landmark annotation.",
            },
            {
              title: "🎥 Video Annotation",
              desc: "Object tracking, frame-by-frame labeling, activity recognition.",
            },
            {
              title: "🎙 Audio Annotation",
              desc: "Speech-to-text, speaker diarization, intent labeling.",
            },
            {
              title: "📝 Text Annotation",
              desc: "NER, sentiment analysis, part-of-speech tagging, intent recognition.",
            },
            {
              title: "🤖 Custom Labeling",
              desc: "Tailored annotation workflows for your industry-specific needs.",
            },
          ].map((item, idx) => (
            <div
              key={idx}
              className="bg-white p-6 rounded-2xl shadow hover:shadow-xl transition-shadow duration-300"
            >
              <h3 className="text-lg font-semibold mb-3 font-orbitron">{item.title}</h3>
              <p className="text-gray-700 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Workflow Section */}
      <section className="py-20 px-6">
        <h2 className="text-4xl font-semibold text-center mb-12 tracking-tight font-orbitron">
          How We Work
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 max-w-6xl mx-auto text-center font-raleway">
          {[
            "Understand requirements",
            "Define annotation guidelines",
            "Human annotators label data",
            "Multi-level quality checks",
            "Deliver AI-ready datasets",
          ].map((step, idx) => (
            <div key={idx} className="p-4">
              <div className="text-4xl mb-2">{`${idx + 1}️⃣`}</div>
              <p className="text-gray-700 leading-relaxed">{step}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 px-6 bg-gray-100">
        <h2 className="text-4xl font-semibold text-center mb-12 tracking-tight font-orbitron">
          Applications of Our Annotation & Labeling
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto font-raleway">
          {[
            {
              title: "🚗 Autonomous Driving",
              desc: "Object detection, pedestrian tracking, traffic sign recognition.",
            },
            {
              title: "🏥 Healthcare AI",
              desc: "Annotated scans, medical imaging, symptom classification.",
            },
            {
              title: "🛒 Retail & E-commerce",
              desc: "Product tagging, visual search, customer behavior analysis.",
            },
            {
              title: "🔒 Security & Surveillance",
              desc: "Face recognition, gesture labeling, anomaly detection.",
            },
            {
              title: "🌐 NLP & Conversational AI",
              desc: "Sentiment analysis, intent recognition, multilingual datasets.",
            },
          ].map((item, idx) => (
            <div
              key={idx}
              className="bg-white p-6 rounded-2xl shadow hover:shadow-xl transition-shadow duration-300"
            >
              <h3 className="font-semibold mb-2 text-lg font-orbitron">{item.title}</h3>
              <p className="text-gray-700 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 text-center bg-gradient-to-r from-indigo-600 to-purple-700 text-white">
        <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight font-orbitron">
          Ready to Train Smarter AI?
        </h2>
        <p className="text-lg max-w-xl mx-auto font-raleway">
          Let’s transform raw data into AI-ready datasets with our world-class
          annotation & labeling services.
        </p>
      </section>
    </div>
  );
}
